import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, X } from "lucide-react";

import { NavigationIcon } from "@/components/icons/NavigationIcon";

const introductions: Record<string, string> = {
  "Planning Tools": "A little less admin. A lot more wedding.",
  "Wedding Venues": "Find a place that feels like you.",
  "Wedding Suppliers": "Meet the people who will bring your day to life.",
  "Advice": "Good advice for every step of the way.",
  "For Businesses": "Your next chapter starts with a great listing.",
};

type MenuLink = { name: string; path: string; featured?: boolean };
type MenuGroup = { name: string; path?: string; submenu?: MenuLink[] };

/** Wide, click-operated navigation: normal links retain browser and keyboard behaviour. */
export function SiteMegaMenu({ groups }: { groups: MenuGroup[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({});
  const location = useLocation();
  useEffect(() => setOpen(null), [location.pathname, location.search]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(null);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, []);
  const active = groups.find(group => group.name === open);
  const close = () => { if (open) triggers.current[open]?.focus(); setOpen(null); };
  return <div ref={root} className="hidden xl:block border-t border-border/60 bg-tint-cream"
    onKeyDown={event => { if (event.key === "Escape" && open) { event.preventDefault(); close(); } }}
    onBlur={event => { if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(null); }}>
    <div className="container mx-auto flex items-center gap-8 px-6">
      {groups.map((group, index) => group.submenu ?
        <button key={group.name} ref={el => { triggers.current[group.name] = el; }}
          type="button" aria-expanded={open === group.name} aria-controls={`site-menu-${index}`}
          onClick={() => setOpen(open === group.name ? null : group.name)}
          className={`min-h-14 inline-flex items-center gap-2 border-b-[3px] text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 ${open === group.name ? "border-primary text-primary" : "border-transparent hover:border-primary/40"}`}>
          {group.name}<ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform ${open === group.name ? "rotate-180" : ""}`} />
        </button> : <Link key={group.name} to={group.path!} className="min-h-14 inline-flex items-center border-b-[3px] border-transparent text-sm font-semibold hover:border-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">{group.name}</Link>)}
    </div>
    {active?.submenu && <section id={`site-menu-${groups.indexOf(active)}`} aria-label={active.name}
      className="absolute inset-x-0 top-full border-b border-border bg-tint-cream shadow-sm">
      <div className="container mx-auto grid grid-cols-[250px_1fr] gap-10 px-6 py-7">
        <div className="bg-primary/10 px-6 py-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-primary">Ever After List</p>
          <h2 className="font-sans text-2xl font-bold tracking-tight text-foreground">{active.name}</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{introductions[active.name] ?? "Make your next step a little simpler."}</p>
        </div>
        <div className="relative pr-12">
          <button type="button" aria-label="Close navigation menu" onClick={close} className="absolute -right-1 -top-2 inline-flex h-11 w-11 items-center justify-center hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"><X className="h-5 w-5" /></button>
          <ul className="grid grid-cols-3 gap-x-7 gap-y-1">
            {active.submenu.map((item, index) => <li key={item.path}><Link to={item.path}
              className={`group flex min-h-14 items-center gap-3 py-3 text-sm font-semibold transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 ${item.featured ? "text-primary" : "text-foreground"}`}>
              <NavigationIcon name={item.name} index={index} />
              <span className="flex-1">{item.name}</span>{item.featured && <ArrowRight aria-hidden="true" className="h-4 w-4" />}
            </Link></li>)}
          </ul>
        </div>
      </div>
    </section>}
  </div>;
}
